import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { usersSelector, productsSelector } from 'src/state/selectors'
import InfoPanel from 'src/global/InfoPanel'
import { Screen } from 'src/screens'
import { getDaysLeft, isPaying, getProductLimit } from '@meniudigital/shared'

export default function ExpiryWarning() {
  const { user } = useSelector(usersSelector)
  const { list: products } = useSelector(productsSelector)
  const navigate = useNavigate()

  // Only show if they have ever had a subscription (subscriptionExpiresAt exists)
  if (!user?.subscriptionExpiresAt) {
    return null
  }

  // Don't show expiry warnings if user has active recurring payment
  if (user?.recurringPayment?.isActive) {
    return null
  }

  const daysLeft = getDaysLeft(user?.subscriptionExpiresAt)
  const isPayingUser = isPaying(user)
  const productCount = products.length
  const productLimit = getProductLimit(user)
  const isOverLimit = productCount > productLimit

  // Expired: Show error only if over product limit
  if (daysLeft <= 0) {
    // Don't show if not over limit - user can continue using for free
    if (!isOverLimit) {
      return null
    }

    return (
      <InfoPanel
        type="error"
        text="Abonamentul tău a expirat. Reînnoiește-l acum pentru a continua să folosești platforma MeniuDigital."
        actionButton={{
          label: 'Reînnoiește abonamentul',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  // Only show warnings if still paying (not expired yet)
  if (!isPayingUser) {
    return null
  }

  // Critical: Less than 15 days - Show error
  if (daysLeft < 15) {
    return (
      <InfoPanel
        type="error"
        text={`Abonamentul tău expiră în ${daysLeft} ${
          daysLeft === 1 ? 'zi' : 'zile'
        }. Reînnoiește abonamentul acum pentru a evita întreruperea serviciilor oferite.`}
        actionButton={{
          label: 'Reînnoiește abonamentul',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  // Warning: Less than 45 days
  if (daysLeft < 45) {
    return (
      <InfoPanel
        type="warning"
        text={`Abonamentul tău expiră în ${daysLeft} zile. Asigură-te că îl reînnoiești la timp pentru a evita întreruperea serviciilor oferite.`}
        actionButton={{
          label: 'Reînnoiește abonamentul',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  // Info: Less than 90 days
  if (daysLeft < 90) {
    return (
      <InfoPanel
        type="info"
        storageId="expiry-90-days"
        storageType="sessionStorage"
        text={`Abonamentul tău expiră în ${daysLeft} de zile. Începând de acum poți reînnoi abonamentul oricând dorești.`}
        actionButton={{
          label: 'Reînnoiește abonamentul',
          onClick: () => navigate(Screen.RenewSubscription),
        }}
      />
    )
  }

  return null
}
